import { createRxDatabase, DexieStorageInternals, RxDatabase, RxStorage } from 'rxdb'
import { addRxPlugin } from 'rxdb'
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup'
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election'
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration-schema'
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie'
import {
  getRxStorageMemory,
  MemoryStorageInternals,
  RxStorageMemoryInstanceCreationOptions,
} from 'rxdb/plugins/storage-memory'
import { RxDBUpdatePlugin } from 'rxdb/plugins/update'

import { Card } from '~/db/types'
import { useSettingsStore } from '~/stores/settings'
import { extractTokenPayload } from '~/utils/extract-token-payload'

import { getSnoozeDelayDaysWithSettings } from '../boundedContext/cards/utils/card'

import { CardsSchema } from './schemas/card'

addRxPlugin(RxDBMigrationPlugin)
addRxPlugin(RxDBCleanupPlugin)
addRxPlugin(RxDBLeaderElectionPlugin)
addRxPlugin(RxDBUpdatePlugin)

type LegacyCard = Omit<Card, 'sourceLanguage' | 'sourceText'> & {
  nativeLanguage?: string
  nativeText?: string
  snoozeCount?: number
  snoozedAt?: number
  snoozedUntil?: number
  sourceLanguage?: string
  sourceText?: string
}

export const initialize: (inMemoryMode?: boolean) => Promise<RxDatabase> = async (inMemoryMode = false) => {
  const storage: RxStorage<
    DexieStorageInternals | MemoryStorageInternals<unknown>,
    RxStorageMemoryInstanceCreationOptions
  > = inMemoryMode ? getRxStorageMemory() : getRxStorageDexie()

  const db = await createRxDatabase({
    name: 'cards_db',
    storage,
    eventReduce: true,
    cleanupPolicy: {},
    ignoreDuplicate: inMemoryMode,
  })

  const settings = useSettingsStore.getState()
  const sourceLanguage = settings.sourceLanguage?.value
  const learnLanguage = settings.learnLanguage?.value

  const userId = extractTokenPayload()?.sub

  await db.addCollections({
    cards: {
      schema: CardsSchema,
      migrationStrategies: {
        2: async (card: LegacyCard) => {
          if (!sourceLanguage || !learnLanguage) return card

          card.nativeLanguage = sourceLanguage
          card.learnLanguage = learnLanguage

          return card
        },
        3: async (card: Card) => {
          if (!userId) return card

          card.userId = userId

          return card
        },
        4: (card: Card) => card,
        5: async (card: LegacyCard) => {
          card.sourceText = card.nativeText ?? card.sourceText ?? ''
          card.sourceLanguage = card.nativeLanguage ?? card.sourceLanguage ?? ''
          delete card.nativeText
          delete card.nativeLanguage

          return card as Card
        },
        6: (card: Card) => card,
        7: (card: LegacyCard) => {
          card.snoozeCount = card.snoozedAt ? 1 : 0

          return card as Card
        },
        8: (card: LegacyCard) => {
          const DAY_IN_MS = 24 * 60 * 60 * 1000
          if (card.snoozedAt)
            card.snoozedUntil =
              card.snoozedAt + getSnoozeDelayDaysWithSettings(card.snoozeCount ?? 0, settings) * DAY_IN_MS

          delete card.snoozedAt

          return card as Card
        },
        9: (card: LegacyCard) => {
          const snoozeCount = card.snoozeCount ?? 0
          const snoozedUntil = card.snoozedUntil

          card.snoozeCountSource = snoozeCount
          card.snoozeCountLearn = snoozeCount
          card.snoozedUntilSource = snoozedUntil
          card.snoozedUntilLearn = snoozedUntil

          delete card.snoozeCount
          delete card.snoozedUntil

          return card as Card
        },
      },
    },
  })

  const preUpdatedAt = (card: Card) => {
    card.updatedAt = new Date().getTime()
  }

  db.collections.cards?.preSave(preUpdatedAt, true)
  db.collections.cards?.preInsert(preUpdatedAt, true)
  db.collections.cards?.preRemove(preUpdatedAt, true)

  return db
}
